import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Trash2 } from "lucide-react";

export const PALETTE = [
  "#1e40af", "#0d9488", "#ea580c", "#7c3aed", "#dc2626", "#0284c7", "#65a30d", "#db2777",
  "#f59e0b", "#0f766e", "#6d28d9", "#b91c1c", "#0369a1", "#4d7c0f", "#be185d", "#334155",
];

export function colorMapFor(keys) {
  const map = {};
  keys.forEach((k, i) => { map[k] = PALETTE[i % PALETTE.length]; });
  return map;
}

export function Panel({ n, title, children, className = "", actions = null }) {
  return (
    <div className={`bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
          <span className="text-[#1e40af] font-bold text-sm uppercase tracking-tight">{title}</span>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

const fmtKwh = (v) =>
  v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 });

export function PowerChart({ series, keys, colorMap, testId = "power-chart" }) {
  if (!series.length) {
    return (
      <div className="h-[380px] flex items-center justify-center text-sm text-slate-400" data-testid={`${testId}-empty`}>
        No data yet — upload an Excel logsheet to see the trend.
      </div>
    );
  }
  return (
    <div className="h-[380px]" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 10, right: 20, bottom: 5, left: 18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} minTickGap={20} />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => Number(v).toLocaleString("en-US")}
            label={{ value: "kWh", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#64748b", fontWeight: 600 } }}
            width={78}
          />
          <Tooltip formatter={(v, name) => [`${fmtKwh(v)} kWh`, name]} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {keys.map((k) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              stroke={colorMap[k]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PowerTable({ items, keys, isAdmin, onDeleteRow, testId = "power-table" }) {
  if (!items.length) {
    return (
      <div className="py-10 text-center text-sm text-slate-400" data-testid={`${testId}-empty`}>
        No data uploaded yet.
      </div>
    );
  }
  const rows = [...items].sort((a, b) => (a.date < b.date ? 1 : -1));
  return (
    <div className="overflow-auto max-h-[480px] rounded-lg border border-slate-200" data-testid={testId}>
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2.5 font-bold whitespace-nowrap">Date</th>
            {keys.map((k) => (
              <th key={k} className="text-right px-3 py-2.5 font-bold whitespace-nowrap">
                {k} <span className="normal-case font-medium text-slate-400">(kWh)</span>
              </th>
            ))}
            {isAdmin && <th className="px-2 py-2.5" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((it) => (
            <tr key={it.date} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="px-3 py-2 font-semibold text-slate-700 whitespace-nowrap tabular-nums">{it.date}</td>
              {keys.map((k) => (
                <td key={k} className="px-3 py-2 text-right tabular-nums text-slate-600 whitespace-nowrap">
                  {fmtKwh(it.values?.[k])}
                </td>
              ))}
              {isAdmin && (
                <td className="px-2 py-2 text-center">
                  <button
                    onClick={() => onDeleteRow(it.date)}
                    data-testid={`${testId}-delete-${it.date}`}
                    title={`Delete ${it.date}`}
                    className="text-slate-300 hover:text-red-600 transition-colors p-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
